import { combineLatest, Observable, of, first, take, switchMap, filter, Subscription } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import deepEqual from 'deep-equal';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, OnChanges, SimpleChanges,
  OnDestroy
} from '@angular/core';

import {
  CmsService, ImageGroup, ImageType, LanguageService, Price, Product, RoutingService, ScriptLoader, WindowRef
} from '@spartacus/core';

import { BrandsEnum, IMedia, IMediaGallery, ISkeeper, ReinsurancesPdpType, SdModalDefaultComponent } from '@seb/web-design-system/core';
import { IInfoBanner, SdInfoBannerComponent } from '@seb/web-design-system/info-banner';
import { IOverview, SdProductOverviewComponent } from '@seb/web-design-system/overview';
import { SdProductSellerModalComponent } from '@seb/web-design-system/product-seller';

import { PaypalBannerComponent } from '@seb/web-common/core/components/static/paypal-banner/paypal-banner.component';
import { StoreLocatorComponent } from '@seb/web-common/core/components/static/store-locator/store-locator.component';
import { OneyModalComponent } from '@seb/web-common/core/components/static/oney-modal/oney-modal.component';

import {
  OmnibusRecommendedPriceModalComponent
} from '@seb/web-common/core/components/static/omnibus-recommended-price-modal/omnibus-recommended-price-modal.component';

import {
  ProductNotificationModalComponent
} from '@seb/web-common/core/components/static/product-notification-modal/product-notification-modal.component';

import {
  SkeeperShoppableContentComponent
} from '@seb/web-common/core/components/static/skeeper-shoppable-content/skeeper-shoppable-content.component';

import {
  BrandsCorner, CustomBaseStore, IadvizeStarterInterface, KlarnaContext, KlarnaData, ReinsuranceFooterModuleInterface,
  ReinsuranceModuleInterface, ServiceOptionsComponentInterface, SkeeperShoppableContentInterface
} from '@seb/web-common/core/models';

import { BaseStoreService, CartFacadeService, ComponentService, TrackingService, ConfigurationService } from '@seb/web-common/core/services';
import { ExternalLogisticianEnum, OrderTypeEnum, PdpTypeEnum, ReinsurancesTypeEnum, RepairabilityEnum } from '@seb/web-common/core/enums';
import { ProductMapper } from '@seb/web-common/core/mappers';
import { getPdpType } from '@seb/web-common/core/utils';

@Component({
  selector: 'wc-product-overview',
  templateUrl: './product-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdInfoBannerComponent,
    SdProductOverviewComponent,
    SdModalDefaultComponent,
    SdProductSellerModalComponent,
    PaypalBannerComponent,
    StoreLocatorComponent,
    ProductNotificationModalComponent,
    OneyModalComponent,
    OmnibusRecommendedPriceModalComponent,
    TranslatePipe,
    SkeeperShoppableContentComponent
  ]
})
export class ProductOverviewComponent implements OnChanges, OnDestroy {
  @Input()
  product?: Product;

  @Input()
  showSkeeper = false;

  @Input()
  objSkeeper: ISkeeper = {};

  @Input()
  isLoading = true;

  @Input()
  brand = BrandsEnum.default;

  @Input()
  isSSR = true;

  @Output()
  hasLoaded = new EventEmitter<void>();

  subscriptions = new Subscription();
  overview: IOverview;
  images: IMediaGallery[] = [];
  isStoreModalOpened = false;
  notifyModalOpened = false;
  quantityDisabled = false;
  klarnaData: KlarnaData;
  isOpenedSellerModal = false;
  isOpenedOneyModal = false;
  price: Price;
  activeLanguage: string;
  displayPayPalBanner = false;
  isOpenedRecommendedModal = false;
  isBannerVisible = false;
  bannerData: IInfoBanner = {};
  madeInData: IMedia;
  currentPage: PdpTypeEnum;
  pdpTypeEnum = PdpTypeEnum;
  isForfait = false;
  isOpenRef = false;
  refurbishedData: IMedia;
  hasVideos = true;
  skeeperShoppableContentData: SkeeperShoppableContentInterface;
  iAdvizeEnabled = false;

  private readonly context = 'Main Product';
  readonly POSITION = 'product';
  private baseStore: CustomBaseStore;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly productMapper: ProductMapper,
    private readonly trackingService: TrackingService,
    private readonly cartFacadeService: CartFacadeService,
    private readonly scriptLoader: ScriptLoader,
    private readonly cdRef: ChangeDetectorRef,
    private readonly cmsService: CmsService,
    private readonly routingService: RoutingService,
    private readonly languageService: LanguageService,
    private readonly viewportScroller: ViewportScroller,
    private readonly baseStoreService: BaseStoreService,
    private readonly windowRef: WindowRef,
    private readonly translateService: TranslateService,
    private readonly componentService: ComponentService,
    private readonly configurationService: ConfigurationService,
    @Inject(DOCUMENT) private readonly document: any
  ) {
    this.baseStore = this.baseStoreService.baseStore;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product']) {
      const { previousValue, currentValue } = changes['product'];

      if ((previousValue !== currentValue) && !deepEqual(previousValue, currentValue)) {
        this.setProduct();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  addToCart(quantity: number): void {
    sessionStorage.setItem('addToCartContext', this.context);
    this.cartFacadeService.addToCartShippingType(this.product, quantity);
  }

  openSellerModal(): void {
    this.isOpenedSellerModal = true;
  }

  notifyMe(): void {
    this.notifyModalOpened = true;
  }

  dividedClick(): void {
    this.isOpenedOneyModal = true;
  }

  isCompatibleClicked(): void {
    this.trackingService.sendEvent('clickCompatibleProducts', {
      productName: this.product.name
    });
  }

  initKlarnaScript(klarna: KlarnaContext): void {
    if (this.document?.querySelector('#klarna-script')) {
      this.klarnaData = {
        price: klarna.price,
        locale: klarna.locale
      };
      this.cdRef.markForCheck();
      return;
    }
    this.scriptLoader.embedScript({
      src: klarna.jsURL,
      attributes: { 'data-client-id': klarna.clientId, id: 'klarna-script' },
      callback: () => {
        this.klarnaData = {
          price: klarna.price,
          locale: klarna.locale
        };
        this.cdRef.markForCheck();
      }
    });
  }

  onRepairabilityInfoClick(): void {
    this.trackingService.sendEvent('clickRepairabilityInfo', {
      productID: this.product.code,
      productName: this.product.name
    });
  }

  onRepairabilityLinkClick(): void {
    this.trackingService.sendEvent('documentationDownload', {
      documentName: this.overview.reparabilityData.calculateLink?.linkName
        ? this.overview.reparabilityData.calculateLink?.linkName
        : this.overview.reparabilityData.calculateLink?.url,
      productName: this.product.name
    });
  }

  onReviewsClicked(): void {
    this.trackingService.sendEvent('clickSeeReviews', {
      productName: this.product.name,
      productID: this.product.code
    });
    this.viewportScroller.scrollToAnchor('block-product-reviews');
  }

  private setProduct(): void {
    if (!this.product) {
      this.klarnaData = undefined;
      this.overview = undefined;
      this.images = [];
      return;
    }

    this.price = this.product.discountedPrice;
    this.iAdvizeEnabled = !!this.baseStoreService.baseStore.enableIadvize;
    this.isForfait = this.quantityDisabled = this.product.productGenre === 'Forfait';
    this.displayPayPalBanner = this.product.onSale && this.productMapper.isInStock(this.product) && !!this.product.price;

    if (this.displayPayPalBanner) {
      this.subscriptions.add(
        this.languageService
          .getActive()
          .pipe(take(1))
          .subscribe((currentLang) => {
            this.activeLanguage = currentLang;
          })
      );
    }

    this.initSkeeperShoppableContent(this.product);

    const cmpPaymentModesForProduct$ = this.cmsService.getContentSlot('PaymentModes').pipe(
      switchMap((contentSlot) => {
        return !!contentSlot && contentSlot.components?.length > 0
          ? combineLatest(
            contentSlot.components?.map(({ uid }) => {
              return this.cmsService.getComponentData(uid).pipe(
                this.componentService?.reloadComponentForArrayProperties(['medias']),
                filter((component: ServiceOptionsComponentInterface) => !!component.medias)
              );
            })
          )
          : of(undefined);
      })
    );

    const cmpIadvizerForProduct$ = this.cmsService.getContentSlot('IAdvizeStarter').pipe(
      switchMap((contentSlot) => {
        return !!contentSlot && contentSlot.components?.length > 0
          ? combineLatest(
            contentSlot.components?.map(({ uid }) => {
              return this.cmsService.getComponentData(uid).pipe(
                filter((component: IadvizeStarterInterface) => !!component.elementId)
              );
            })
          )
          : of(undefined);
      })
    );

    const cmpReinsurances$ = this.cmsService.getContentSlot('Reinsurances').pipe(
      switchMap((component) => {
        return !!component.components && component.components.length > 0
          ? this.cmsService.getComponentData(component.components[0].uid).pipe(
            filter((cmp) => !!cmp),
            switchMap((cmp) => this.componentService.getLazyComponentData(cmp, 'components'))
          )
          : of(undefined);
      })
    );

    const cmpReinsurancesWithLinks$ = this.isForfait
      ? of(undefined)
      : cmpReinsurances$.pipe(
        switchMap((cmpReinsurances: ReinsuranceModuleInterface) => {
          const cmpLinks: Observable<ReinsuranceModuleInterface>[] = (
            !!cmpReinsurances?.componentsObjects && cmpReinsurances?.componentsObjects.length > 0
              ? cmpReinsurances?.componentsObjects.map((cmp) => {
                return this.componentService.getLazyComponentData(cmp, 'components').pipe(filter((cmp) => !!cmp));
              })
              : of([])
          ) as Observable<ReinsuranceModuleInterface>[];
          return combineLatest([of(cmpReinsurances?.title), combineLatest(cmpLinks)]);
        })
      );

    this.subscriptions.add(
      combineLatest([
        this.cmsService.getCurrentPage(),
        this.routingService.getRouterState(),
        cmpReinsurancesWithLinks$,
        cmpPaymentModesForProduct$,
        cmpIadvizerForProduct$,
        this.route.queryParamMap
      ])
        .pipe(first(([p, s]) => !!p && !!s))
        .subscribe(
          ([
            page,
            routerState,
            cmpReinsurancesServicesAndWarranty,
            paymentModesForProductCmps,
            cmpIadvizerForProduct,
            queryParams
          ]) => {
            const isNewPriceManagementActivated = this.baseStore.activateNewPriceManagement;
            const brandFound = this.baseStoreService.getBrand(this.product.brand);

            if (!this.baseStore.ratingAndReviewsIdWebsite) {
              // AVF not activated
              this.product.gsebAverageRating = undefined;
              this.product.gsebNumberOfReviews = undefined;
            }

            let _cmpReinsurancesServicesAndWarranty: ReinsuranceFooterModuleInterface = {};
            let _cmpReinsurancesServicesAndWarrantyReinsurances = [];

            if (cmpReinsurancesServicesAndWarranty) {
              const isReparability = this.product.reparabilityIncluded === RepairabilityEnum.YES;
              const repairabilityStatus = this.product.repairabilityStatus;

              _cmpReinsurancesServicesAndWarrantyReinsurances = cmpReinsurancesServicesAndWarranty[1]?.filter((cmp) => {
                if (cmp.ReinsuranceType === ReinsurancesTypeEnum.REPAIRABILITY && isReparability) {
                  if (repairabilityStatus) {
                    const isRepairability10Y = repairabilityStatus.includes('_10Y_');
                    const isRepairability15Y = repairabilityStatus.includes('_15Y_');
                    if (isRepairability15Y) {
                      cmp.headline = cmp.headline.replace('10', '15');
                    } else if (isRepairability10Y) {
                      cmp.headline = cmp.headline.replace('15', '10');
                    }
                  }
                }
                return isReparability
                  ? cmp.ReinsuranceType === ReinsurancesTypeEnum.CLIENT_SERVICE ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.EXTENDED_WARRANTY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.REPAIRABILITY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.WARRANTY
                  : cmp.ReinsuranceType === ReinsurancesTypeEnum.CLIENT_SERVICE ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.EXTENDED_WARRANTY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.WARRANTY;
              });

              _cmpReinsurancesServicesAndWarranty = {
                headline: cmpReinsurancesServicesAndWarranty[0],
                componentsObjects: _cmpReinsurancesServicesAndWarrantyReinsurances
              };
            }

            const url = this.route.snapshot?.url[0]?.path;
            this.currentPage = getPdpType(url);

            const isServicePage =
              this.currentPage === this.pdpTypeEnum.ACCESSORY ||
              this.currentPage === this.pdpTypeEnum.IFU ||
              this.currentPage === this.pdpTypeEnum.FAQ;

            //Filter Component to the one corresponding to this product external logistician
            const paymentModesComponent: ServiceOptionsComponentInterface = paymentModesForProductCmps?.find(
              (componentData: any) =>
                this.product.externalLogistician === ExternalLogisticianEnum.PFS
                  ? componentData.orderType === OrderTypeEnum.EXTLOG
                  : componentData.orderType === OrderTypeEnum.SAP
            );

            const config = this.configurationService.config;
            const _activeBrands = this.baseStore.houseOfBrandConfig?.brand;
            const isHob = _activeBrands?.length > 0;

            this.overview = this.productMapper.productToIOverview(
              this.product,
              page,
              routerState,
              brandFound,
              this.baseStore.displayTaxesIncluded,
              _cmpReinsurancesServicesAndWarranty,
              paymentModesComponent,
              this.isForfait,
              isServicePage,
              isNewPriceManagementActivated,
              config?.pdp,
              isHob,
              cmpIadvizerForProduct?.[0]?.elementId
            );

            if (
              brandFound &&
              !this.product.isAccessory &&
              this.translateService.instant('product.brandcorner.banner.text') !== 'product.brandcorner.banner.text' &&
              this.translateService.instant('product.brandcorner.banner.text')?.trim()?.length > 0
            ) {
              this.showBrandCornerBanner(brandFound, this.product.name);
            }

            this.madeInData = this.productMapper.getBadge(this.product);
            this.refurbishedData = this.productMapper.getRefurbishedLogo(this.product);

            setTimeout(() => (this.isOpenRef = queryParams?.get('openRef') === 'true'));

            this.cdRef.markForCheck();
            this.hasLoaded.emit();
          }
        )
    );

    if (this.product.klarnaContext) {
      this.initKlarnaScript(this.product.klarnaContext);
    }

    this.images = ((this.product.images?.[ImageType.GALLERY] as ImageGroup[]) ?? [])
      .filter((value) => !!value && Object.keys(value).length !== 0)
      .map((imageGroup) => ({
        extra: imageGroup.extra ?? undefined,
        icon: imageGroup.icon ?? undefined,
        main: imageGroup.extra ?? imageGroup.main ?? undefined,
        original: imageGroup.original ?? imageGroup.extra ?? imageGroup.main ?? undefined,
        thumbnail: imageGroup.thumbnail ?? imageGroup.icon ?? undefined
      }));
  }

  openRecommendedPriceModal(): void {
    this.isOpenedRecommendedModal = true;
  }

  onProductFeatureClick(): void {
    this.trackingService.sendEvent('clickProductBenefits', {
      productName: this.product.name
    });
  }

  onReinsurancesPdpClick(event): void {
    if (event === ReinsurancesPdpType.securedPayement) {
      this.trackingService.sendEvent('clickProductPaymentInfo', {
        productName: this.product.name
      });
    }
  }

  showBrandCornerBanner(brandFound: BrandsCorner, productName: string): void {
    this.isBannerVisible = true;

    this.bannerData = {
      siteBrand: this.baseStore.brandSite,
      brand: brandFound.id,
      itemName: productName,
      website: this.windowRef.location.hostname
    };
  }

  bannerVisibilityChange(): void {
    this.isBannerVisible = false;
  }

  initSkeeperShoppableContent(product: Product) {
    const titleSection = this.translateService.instant('shoppable.section.title');
    this.skeeperShoppableContentData = {
      title: titleSection,
      product: product?.code
    };
    this.cdRef.detectChanges();
  }

  checkHasVideos(event) {
    this.hasVideos = event;
  }
}
